/**
 * Render animasi SVG jadi video dengan cara:
 * 1. Seek GSAP timeline ke tiap frame time (via engine.seek)
 * 2. Serialize elemen <svg> yang sedang dianimasikan (sudah termasuk transform hasil GSAP)
 * 3. Gambar ke <canvas> tersembunyi
 * 4. Rekam canvas itu pakai MediaRecorder + captureStream, frame-by-frame (bukan real-time),
 *    supaya durasi video akurat & tidak tergantung performa device
 *
 * CATATAN JUJUR SOAL FORMAT:
 * Browser (Chrome/Firefox/Edge) hanya bisa native encode ke WebM lewat MediaRecorder.
 * MP4 native cuma didukung di Safari. Fungsi ini otomatis pilih MP4 kalau browser
 * support, kalau tidak fallback ke WebM. WebM tetap bisa diputar di hampir semua
 * platform (browser, VLC, upload ke medsos/Adobe Stock dll), tapi bukan literal
 * ".mp4". Kalau butuh .mp4 yang benar-benar mp4 di semua browser, perlu tambahan
 * library encoder (mp4-muxer / ffmpeg.wasm) — bisa disusulkan kalau dibutuhkan.
 */

export function pickBestMimeType() {
  const candidates = [
    "video/mp4;codecs=avc1", // Safari
    "video/mp4",
    "video/webm;codecs=vp9",
    "video/webm;codecs=vp8",
    "video/webm",
  ];
  for (const type of candidates) {
    if (typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported(type)) {
      return type;
    }
  }
  return null;
}

async function drawSvgFrame(svgElement, ctx, width, height, scale, backgroundColor) {
  const clone = svgElement.cloneNode(true);
  clone.setAttribute("width", width);
  clone.setAttribute("height", height);
  clone.querySelectorAll("[style]").forEach((el) => {
    el.style.outline = "none";
  });

  const xml = new XMLSerializer().serializeToString(clone);
  const svgBlob = new Blob([xml], { type: "image/svg+xml;charset=utf-8" });
  const url = URL.createObjectURL(svgBlob);

  try {
    const img = new Image();
    await new Promise((resolve, reject) => {
      img.onload = resolve;
      img.onerror = () => reject(new Error("Gagal render frame SVG."));
      img.src = url;
    });
    if (backgroundColor) {
      ctx.fillStyle = backgroundColor;
      ctx.fillRect(0, 0, width * scale, height * scale);
    } else {
      // transparan: bersihkan canvas tiap frame, jangan isi warna apa pun
      ctx.clearRect(0, 0, width * scale, height * scale);
    }
    ctx.drawImage(img, 0, 0, width * scale, height * scale);
  } finally {
    URL.revokeObjectURL(url);
  }
}

/**
 * @param {SVGSVGElement} svgElement - elemen <svg> live yang sedang dianimasikan GSAP
 * @param {{seek:(t:number)=>void}} engine - instance dari useAnimationEngine
 * @param {number} duration - total durasi animasi (detik)
 * @param {object} options
 * @param {number} options.fps
 * @param {number} options.scale - faktor resolusi (2 = 2x ukuran asli SVG)
 * @param {(progress:number)=>void} options.onProgress - 0..1
 * @returns {Promise<{blob: Blob, mimeType: string, extension: string}>}
 */
export async function exportSvgToVideo(svgElement, engine, duration, options = {}) {
  const { fps = 30, scale = 2, backgroundColor = "#ffffff", onProgress } = options;

  if (!svgElement) throw new Error("Elemen SVG tidak ditemukan.");
  if (typeof MediaRecorder === "undefined") {
    throw new Error("Browser ini tidak mendukung MediaRecorder API.");
  }

  const mimeType = pickBestMimeType();
  if (!mimeType) {
    throw new Error("Browser ini tidak mendukung encoding video sama sekali.");
  }
  const isMp4 = mimeType.startsWith("video/mp4");

  const width = svgElement.viewBox.baseVal.width || svgElement.clientWidth || 400;
  const height = svgElement.viewBox.baseVal.height || svgElement.clientHeight || 400;

  const canvas = document.createElement("canvas");
  canvas.width = width * scale;
  canvas.height = height * scale;
  const ctx = canvas.getContext("2d");

  const stream = canvas.captureStream(0);
  const track = stream.getVideoTracks()[0];

  const recorder = new MediaRecorder(stream, {
    mimeType,
    videoBitsPerSecond: 8_000_000,
  });

  const chunks = [];
  recorder.ondataavailable = (e) => {
    if (e.data && e.data.size > 0) chunks.push(e.data);
  };

  const stopped = new Promise((resolve) => {
    recorder.onstop = resolve;
  });

  recorder.start();

  const totalFrames = Math.max(1, Math.ceil(duration * fps));

  for (let i = 0; i <= totalFrames; i++) {
    const t = Math.min(i / fps, duration);
    engine.seek(t);
    await new Promise((r) => requestAnimationFrame(r));
    await drawSvgFrame(svgElement, ctx, width, height, scale, backgroundColor);
    if (track.requestFrame) track.requestFrame();
    await new Promise((r) => setTimeout(r, 1000 / fps));
    onProgress?.(i / totalFrames);
  }

  recorder.stop();
  const blob = await stopped.then(() => new Blob(chunks, { type: mimeType }));

  return {
    blob,
    mimeType,
    extension: isMp4 ? "mp4" : "webm",
  };
}
